/**
 * @fileoverview Toolbar controls for the template canvas build and preview
 * modes.
 * @module template-builder
 *
 * Responsibilities:
 * - Switch between Build and Preview modes.
 * - Expose canvas-level visual toggles such as rule connection lines.
 * - Show lightweight canvas statistics and AI entry points.
 *
 * Dependencies:
 * - lucide-react icons
 *
 * Architecture:
 * - Presentation-only component; state and persistence are owned by the host
 *   TemplateCanvas component and its local-first services.
 *
 * Notes:
 * - Disabled toolbar actions must explain why they are unavailable.
 */
import { Eye, Pencil, Layers, Share2, Sparkles, Lock } from 'lucide-react';

export type CanvasMode = 'build' | 'preview';

/**
 * @contract TemplateCanvasToolbarProps
 *
 * Purpose:
 * - Defines the state and callbacks needed by the canvas toolbar.
 *
 * Fields:
 * - mode: Active canvas mode.
 * - onModeChange: Switches canvas mode.
 * - formCount: Number of forms rendered by the current template.
 * - ruleCount: Number of rules available for line rendering.
 * - onOpenFieldPalette: Optional callback for opening the field palette.
 * - fieldPaletteOpen: Whether the field palette is currently visible.
 * - showRuleLines: Whether rule connection lines are currently shown.
 * - onToggleRuleLines: Toggles rule line rendering.
 * - hasAI: Whether AI features are available.
 * - aiOpen: Whether the AI panel is open.
 * - onToggleAI: Toggles the AI panel.
 * - aiTokensUsed: Tokens used in the current AI session.
 * - aiSessionBudget: Token budget for the current AI session.
 *
 * Rules:
 * - This component must not call APIs or mutate template data directly.
 *
 * Sync:
 * - Local-first synchronization is handled by TemplateCanvas consumers.
 */
export interface TemplateCanvasToolbarProps {
  mode: CanvasMode;
  onModeChange: (mode: CanvasMode) => void;
  formCount: number;
  ruleCount: number;
  onOpenFieldPalette?: () => void;
  fieldPaletteOpen?: boolean;
  showRuleLines: boolean;
  onToggleRuleLines: () => void;
  hasAI?: boolean;
  aiOpen?: boolean;
  onToggleAI?: () => void;
  aiTokensUsed?: number;
  aiSessionBudget?: number;
}

export function TemplateCanvasToolbar({
  mode,
  onModeChange,
  formCount,
  ruleCount,
  onOpenFieldPalette,
  fieldPaletteOpen,
  showRuleLines,
  onToggleRuleLines,
  hasAI,
  aiOpen,
  onToggleAI,
  aiTokensUsed = 0,
  aiSessionBudget = 10000,
}: TemplateCanvasToolbarProps) {
  return (
    <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-3">
      {/* Mode toggle */}
      <div className="flex items-center gap-0 rounded-md border border-gray-200 overflow-hidden">
        <button
          onClick={() => onModeChange('build')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
            mode === 'build'
              ? 'bg-(--brand-primary) text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Pencil size={12} />
          Build
        </button>
        <button
          onClick={() => onModeChange('preview')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
            mode === 'preview'
              ? 'bg-(--brand-primary) text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Eye size={12} />
          Preview
        </button>
      </div>

      <div className="h-4 w-px bg-gray-200" />

      {/* Build-mode toggles */}
      {mode === 'build' && (
        <div className="flex items-center gap-1.5">
          {onOpenFieldPalette && (
            <button
              onClick={onOpenFieldPalette}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
                fieldPaletteOpen
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Layers size={12} />
              Fields
            </button>
          )}
          {/* Section 2.6: Lines button disabled when no rules exist. */}
          <button
            onClick={onToggleRuleLines}
            disabled={ruleCount === 0}
            aria-disabled={ruleCount === 0}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
              ruleCount === 0
                ? 'bg-white border-gray-200 text-gray-300 cursor-not-allowed'
                : showRuleLines
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
            title={ruleCount === 0 ? 'Rule lines are unavailable until at least one rule is defined in the Rules panel' : 'Toggle rule connection lines'}
          >
            {ruleCount === 0 ? <Lock size={12} /> : <Share2 size={12} />}
            Lines
            {ruleCount > 0 && (
              <span className={`text-[9px] font-semibold ${showRuleLines ? 'text-indigo-400' : 'text-gray-400'}`}>
                {ruleCount}
              </span>
            )}
          </button>
          {hasAI && onToggleAI && (
            <button
              onClick={onToggleAI}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
                aiOpen
                  ? 'bg-purple-50 border-purple-200 text-purple-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              title="AI Assistant"
            >
              <Sparkles size={12} />
              AI
              {aiTokensUsed > 0 && aiSessionBudget > 0 && (
                <span className={`text-[9px] font-semibold ml-0.5 ${
                  aiTokensUsed / aiSessionBudget > 0.8 ? 'text-red-500' : 'text-purple-400'
                }`}>
                  {Math.round(aiTokensUsed / aiSessionBudget * 100)}%
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center gap-3 ml-1 text-xs text-gray-400">
        <span>{formCount} form{formCount !== 1 ? 's' : ''}</span>
        {ruleCount > 0 && (
          <span className="text-purple-500">{ruleCount} rule{ruleCount !== 1 ? 's' : ''}</span>
        )}
      </div>

      <div className="flex-1" />

      {/*
        Section 2.8.3: Settings dropdown removed. Template name/description
        live inline in the TemplateDetailPage header; sharing lives on the
        prominent Share button in that same header. No equivalent UI here.
      */}
    </div>
  );
}

export default TemplateCanvasToolbar;
